<template>
  <component :is="isBrand('bcom') ? 'h5' : 'h4'" v-if="showTitle" class="margin-top-m hide-for-print"> When will I get my refund? </component>
  <p
    class="hide-for-print margin-bottom-s"
    :class="{
      'margin-top-xxs': !showStepper,
    }"
  >
    You’ll get your refund within
    <span class="heavy" v-text="isBorisReturn ? '10 business days' : '2 weeks'"></span>.
  </p>
  <div id="refund-info" class="hide-for-print" :aria-live="ariaLive">
    <div class="flex-container" :class="showStepper ? 'margin-bottom-m medium-margin-bottom-xl' : 'margin-bottom-s'">
      <template v-if="showStepper">
        <div>
          <div class="circle">
            <span>1</span>
          </div>
          <div class="vertical-line"></div>
        </div>
        <p class="left-pane"></p>
      </template>
      <div class="right-pane margin-bottom-xxs">
        <div class="large"><span v-if="!showStepper">1.</span> {{ pointOne.title }}</div>
        <div v-text="pointOne.text"></div>
      </div>
    </div>
    <div v-if="pointTwo" class="flex-container" :class="showStepper ? 'margin-bottom-m medium-margin-bottom-xl' : 'margin-bottom-s'">
      <template v-if="showStepper">
        <div>
          <div class="circle">
            <span>2</span>
          </div>

          <div v-if="showPointThree" class="vertical-line"></div>
        </div>
        <p class="left-pane"></p>
      </template>
      <div class="right-pane margin-bottom-xxs">
        <div>
          <div class="large"><span v-if="!showStepper">2.</span> {{ pointTwo.title }}</div>
          <div v-text="pointTwo.text"></div>
        </div>
      </div>
    </div>
    <div v-if="showPointThree" class="flex-container" :class="showStepper ? 'margin-bottom-m medium-margin-bottom-xl' : 'margin-bottom-s'">
      <template v-if="showStepper">
        <div class="circle">
          <span v-text="pointTwo ? '3' : '2'"></span>
        </div>
        <p class="left-pane"></p>
      </template>
      <div class="right-pane margin-bottom-xxs">
        <div class="large">
          <span v-if="!showStepper" v-text="pointTwo ? '3.' : '2.'"></span>
          {{ pointThree.title }}
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <div v-html="pointThree.text"></div>
      </div>
    </div>
    <div id="notes" class="small margin-top-xxs gray-1-color">
      <div
        v-if="!isMarketplaceOrder || (isBrand('bcom') && isBorisReturn)"
        class="margin-bottom-s"
        v-text="notes.jewelry(props.returnDetails?.returnShipment?.fineJewelryProcessingDuration || defaultJewelryDuration)"
      ></div>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <div v-if="isBrand('mcom')" v-html="notes.starMoney"></div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { refundInfo, REFUND_METHOD } from 'config';
import { RETURNS_DURATIONS } from '#shared/constants';
import useBrand from '@/composables/useBrand';

const props = defineProps({
  returnDetails: {
    type: Object,
    required: true,
  },
  showStepper: {
    type: Boolean,
    required: false,
    default: false,
  },
  ariaLive: {
    type: String,
    required: false,
    default: 'polite',
  },
  showTitle: {
    type: Boolean,
    required: false,
    default: false,
  },
  isBorisReturn: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const { isMcom, isBcom } = useBrand();

const isBrand = (brand) => {
  return brand === 'mcom' ? isMcom : isBcom;
};

const defaultRefundProcessingDuration = RETURNS_DURATIONS.DEFAULT_REFUND_PROCESSING_DURATION;
const defaultGiftCardDuration = RETURNS_DURATIONS.DEFAULT_GIFTCARD_DURATION;
const defaultInTransitDuration = RETURNS_DURATIONS.DEFAULT_INTRANSIT_DURATION;
const defaultJewelryDuration = RETURNS_DURATIONS.DEFAULT_JEWELRY_DURATION;

const isMarketplaceOrder = computed(() => props.returnDetails.isMkpOrder ?? false);
const isStoreCreditRefund = computed(() => props.returnDetails?.refundMethod === REFUND_METHOD.WALLET);

const pointOne = computed(() => {
  const inTransitDuration = props.returnDetails?.inTransitDuration || defaultInTransitDuration;
  const obj = {
    title: refundInfo.one.title.default,
    text: refundInfo.one.text.default(inTransitDuration),
  };

  if (isMarketplaceOrder.value) {
    obj.text = refundInfo.one.text.mkp(props.isBorisReturn);
  }
  if (isStoreCreditRefund.value) {
    const textConfig = (isMarketplaceOrder.value && refundInfo.one.text.storeCreditMkp) || refundInfo.one.text.storeCredit;
    if (textConfig) {
      obj.text = typeof textConfig === 'function' ? textConfig(inTransitDuration) : textConfig;
    }
  }
  return obj;
});

const pointTwo = computed(() => {
  const refundWindow = props.returnDetails?.refundWindow || defaultRefundProcessingDuration;
  const obj = {
    title: refundInfo.two.title.default,
    text: refundInfo.two.text.default(refundWindow),
  };
  if (isMarketplaceOrder.value) {
    obj.text = refundInfo.two.text.mkp;
  }
  if (isStoreCreditRefund.value) {
    const textConfig = (isMarketplaceOrder.value && refundInfo.two.text.storeCreditMkp) || refundInfo.two.text.storeCredit;
    if (textConfig) {
      obj.text = typeof textConfig === 'function' ? textConfig(refundWindow) : textConfig;
    }
  }
  return obj;
});

const pointThree = computed(() => {
  const giftCardDuration = props.returnDetails?.giftCardDuration || defaultGiftCardDuration;
  const obj = {
    title: refundInfo.three.title.default,
    text: refundInfo.three.text.default(giftCardDuration),
  };
  if (isMarketplaceOrder.value) {
    obj.text = refundInfo.three.text.mkp(props.isBorisReturn);
  }

  if (REFUND_METHOD.EGIFT === props.returnDetails.refundMethod) {
    obj.text = refundInfo.three.text.vgc;
  }
  if (isStoreCreditRefund.value) {
    const textConfig = (isMarketplaceOrder.value && refundInfo.three.text.storeCreditMkp) || refundInfo.three.text.storeCredit;
    if (textConfig) {
      obj.text = typeof textConfig === 'function' ? textConfig(giftCardDuration) : textConfig;
    }
  }
  return obj;
});

const showPointThree = computed(() => {
  if (REFUND_METHOD.GIFT === props.returnDetails.refundMethod) return false;
  return true;
});

const notes = computed(() => refundInfo.notes);
</script>

<style lang="scss" scoped>
.left-pane {
  width: 5%;
}
.right-pane {
  width: 93%;
}
.circle {
  width: rem-calc(22);
  height: rem-calc(21);
  border-radius: 100%;
  text-align: center;
  font-size: rem-calc(12);
  font-weight: bold;
  border: 1px solid $black;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: $white;
}
.vertical-line {
  border-left: rem-calc(2) solid $black;
  margin-left: rem-calc(11);
  height: calc(100% + 3rem);
  @media screen and (max-width: 600px) {
    height: calc(100% + 1rem);
  }
}
</style>
